import { Check, X } from 'lucide-react'
import React, { useMemo, useState } from 'react'

import { cn } from '~in-app/lib/utils'

import {
  Badge,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './shadcn-ui'

export interface Option {
  label: string
  value: string
}

interface MultiSelectPopoverProps {
  id?: string
  options: Option[]
  value: Option[]
  onChange: (value: Option[]) => void
  label?: string
  placeholder?: string
  disabled?: boolean
}

const MultiSelectDropdown: React.FC<MultiSelectPopoverProps> = ({
  id,
  options,
  value,
  onChange,
  label,
  placeholder = 'Select options...',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options
    return options.filter(opt =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [options, searchTerm])

  const handleToggle = (opt: Option) => {
    const isSelected = value.some(v => v.value === opt.value)
    if (isSelected) {
      onChange(value.filter(v => v.value !== opt.value))
    }
    else {
      onChange([...value, opt])
    }
  }

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }

  return (
    <div className="w-full">
      {
        label && (
          <label
            className="mb-1 block text-sm font-medium text-gray-700"
            htmlFor={id}
          >
            {label}
          </label>
        )
      }

      <Popover
        open={open}
        onOpenChange={setOpen}
      >
        <PopoverTrigger asChild>
          <div
            id={id}
            className={
              cn(
                'flex min-h-[40px] w-full flex-wrap items-center justify-start rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                open && 'ring-2 ring-ring ring-offset-2',
              )
            }
          >
            {
              value.length === 0 && (
                <span className="text-muted-foreground">{placeholder}</span>
              )
            }

            {
              value.map(v => (
                <Badge
                  key={v.value}
                  className="mb-1 mr-1 flex items-center gap-1 bg-muted text-sm font-normal text-foreground"
                >
                  {v.label}

                  <button
                    className="rounded-full hover:text-destructive"
                    onClick={
                      (e) => {
                        e.stopPropagation()
                        handleToggle(v)
                      }
                    }
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))
            }

            {
              value.length > 0 && (
                <button
                  className="ml-auto text-muted-foreground hover:text-foreground"
                  type="button"
                  onClick={handleClearAll}
                >
                  <X className="size-4" />
                </button>
              )
            }
          </div>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-[300px] p-0"
          sideOffset={4}
        >
          <Command>
            <CommandInput
              className="h-9"
              placeholder="Search..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />

            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>

                <CommandGroup>
                  {
                    filteredOptions.map((opt) => {
                      const isSelected = value.some(v => v.value === opt.value)
                      return (
                        <CommandItem
                          key={opt.value}
                          className="cursor-pointer"
                          onSelect={() => handleToggle(opt)}
                        >
                          <Check
                            className={
                              cn(
                                'mr-2 h-4 w-4',
                                isSelected ? 'opacity-100' : 'opacity-0',
                              )
                            }
                          />

                          {opt.label}
                        </CommandItem>
                      )
                    })
                  }
                </CommandGroup>
              </CommandList>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default MultiSelectDropdown
